/**
 * Secure Key Caching Service
 * Uses WebCrypto API and sessionStorage for per-tab ElGamal keypair caching.
 * Connection markers remain in localStorage so first-time connection UX survives reloads,
 * but private-key material and its wrapping key are cleared when the browser session ends.
 */

const CACHE_PREFIX = 'ConfidentialBalances_';
const CACHE_KEY_STORAGE_KEY = 'secureCacheKey';
const MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

const keyStorage = () => sessionStorage;
const historyStorage = () => localStorage;

class SecureKeyCache {
    constructor() {
        this.cryptoKey = null;
    }

    /**
     * Initialize the secure key cache
     */
    async initialize() {
        try {
            // Check if we have an encryption key
            const hasEncryptionKey = !!keyStorage().getItem(CACHE_KEY_STORAGE_KEY);
            
            // Generate or retrieve the encryption key
            this.cryptoKey = await this.getOrCreateCryptoKey();
            
            // If we just generated a new encryption key (didn't have one before),
            // we need to clear any old encrypted keypairs that were encrypted with a different key
            // This handles tab/session resets.
            if (!hasEncryptionKey) {
                // Clear all cached keypairs since they were encrypted with a different key
                const keysToRemove = [];
                const storage = keyStorage();
                for (let i = 0; i < storage.length; i++) {
                    const key = storage.key(i);
                    if (key && key.startsWith(CACHE_PREFIX) && !key.includes('connected_')) {
                        keysToRemove.push(key);
                    }
                }
                keysToRemove.forEach(key => storage.removeItem(key));
            }

            this.clearLegacyPersistentKeyMaterial();
            
            return true;
        } catch (error) {
            console.error('Failed to initialize secure key cache:', error);
            return false;
        }
    }

    /**
     * Remove sensitive key material left by older localStorage-based cache versions.
     * Non-sensitive connection markers are intentionally preserved for UX continuity.
     */
    clearLegacyPersistentKeyMaterial() {
        try {
            const keysToRemove = [];
            const storage = historyStorage();
            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (
                    key === CACHE_KEY_STORAGE_KEY ||
                    (key && key.startsWith(CACHE_PREFIX) && !key.includes('connected_'))
                ) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => storage.removeItem(key));
        } catch (error) {
            console.error('[KeyCache] Failed to clear legacy persistent key material:', error);
        }
    }

    /**
     * Get or create the encryption key for secure storage
     */
    async getOrCreateCryptoKey() {
        try {
            // Try to import existing key from sessionStorage
            const storedKeyData = keyStorage().getItem(CACHE_KEY_STORAGE_KEY);
            if (storedKeyData) {
                try {
                    const keyData = JSON.parse(storedKeyData);
                    
                    // keyData should be an array from Array.from(exportedKey)
                    // Convert to Uint8Array directly (it's already an array of numbers)
                    let keyBytes;
                    if (Array.isArray(keyData)) {
                        keyBytes = new Uint8Array(keyData);
                    } else {
                        // Fallback: try Object.values if it's an object
                        keyBytes = new Uint8Array(Object.values(keyData));
                    }
                    
                    if (keyBytes.length !== 32) {
                        throw new Error(`Invalid key length: ${keyBytes.length}, expected 32`);
                    }
                    
                    const cryptoKey = await crypto.subtle.importKey(
                        'raw',
                        keyBytes,
                        { name: 'AES-GCM' },
                        false,
                        ['encrypt', 'decrypt']
                    );
                    return cryptoKey;
                } catch (importError) {
                    console.error('[KeyCache] ✗ Error importing key data:', importError);
                    console.error('[KeyCache] Import error details:', {
                        name: importError.name,
                        message: importError.message,
                        stack: importError.stack
                    });
                    console.error('[KeyCache] Key data preview:', storedKeyData.substring(0, 200));
                    // If import fails, we'll generate a new key below
                    // But first, clear the corrupted key data
                    keyStorage().removeItem(CACHE_KEY_STORAGE_KEY);
                }
            } else {
            }
        } catch (error) {
            console.error('[KeyCache] Error accessing sessionStorage for encryption key:', error);
        }

        // Generate new encryption key
        const cryptoKey = await crypto.subtle.generateKey(
            { name: 'AES-GCM', length: 256 },
            true, // extractable for storage
            ['encrypt', 'decrypt']
        );

        // Export and store the key in sessionStorage (cleared when the browser session ends)
        try {
            const exportedKey = await crypto.subtle.exportKey('raw', cryptoKey);
            // exportedKey is an ArrayBuffer, convert to Uint8Array then to array
            const keyBytes = new Uint8Array(exportedKey);
            const keyArray = Array.from(keyBytes);
            keyStorage().setItem(CACHE_KEY_STORAGE_KEY, JSON.stringify(keyArray));
        } catch (storeError) {
            console.error('[KeyCache] Failed to store encryption key:', storeError);
            // Continue anyway - the key is in memory
        }

        return cryptoKey;
    }

    /**
     * Store a keypair securely
     */
    async storeKeypair(account, chainId, verifyingContract, keypair) {
        if (!this.cryptoKey) {
            throw new Error('Secure key cache not initialized');
        }

        try {
            // Encrypt the keypair data
            const keypairData = JSON.stringify({
                public_key: keypair.public_key,
                private_key: keypair.private_key,
                timestamp: Date.now()
            });

            const iv = crypto.getRandomValues(new Uint8Array(12));
            const encryptedData = await crypto.subtle.encrypt(
                { name: 'AES-GCM', iv },
                this.cryptoKey,
                new TextEncoder().encode(keypairData)
            );

            // Store in sessionStorage (cleared when the browser session ends)
            const cacheKey = `${CACHE_PREFIX}${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            const cacheEntry = {
                encryptedData: Array.from(new Uint8Array(encryptedData)),
                iv: Array.from(iv),
                timestamp: Date.now()
            };

            keyStorage().setItem(cacheKey, JSON.stringify(cacheEntry));
            
            return true;
        } catch (error) {
            console.error('Failed to store keypair:', error);
            return false;
        }
    }

    /**
     * Retrieve a keypair securely
     */
    async retrieveKeypair(account, chainId, verifyingContract) {
        if (!this.cryptoKey) {
            console.error('[KeyCache] Crypto key not initialized');
            throw new Error('Secure key cache not initialized');
        }

        try {
            const cacheKey = `${CACHE_PREFIX}${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            const storedData = keyStorage().getItem(cacheKey);
            
            if (!storedData) {
                // Debug: list all keys with our prefix
                const allKeys = [];
                const storage = keyStorage();
                for (let i = 0; i < storage.length; i++) {
                    const key = storage.key(i);
                    if (key && key.startsWith(CACHE_PREFIX)) {
                        allKeys.push(key);
                    }
                }
                return null;
            }

            const result = JSON.parse(storedData);

            // Check if cache entry is not too old (24 hours)
            const age = Date.now() - result.timestamp;
            const ageHours = age / (60 * 60 * 1000);
            if (age > MAX_AGE) {
                // Cache expired, remove it
                await this.removeKeypair(account, chainId, verifyingContract);
                return null;
            }

            // Decrypt the keypair data
            
            const encryptedData = new Uint8Array(result.encryptedData);
            const iv = new Uint8Array(result.iv);
            
            let decryptedData;
            try {
                decryptedData = await crypto.subtle.decrypt(
                    { name: 'AES-GCM', iv },
                    this.cryptoKey,
                    encryptedData
                );
            } catch (decryptError) {
                console.error('[KeyCache] ✗ Decryption failed:', decryptError);
                console.error('[KeyCache] Decrypt error details:', {
                    name: decryptError.name,
                    message: decryptError.message,
                    stack: decryptError.stack
                });
                // This usually means the encryption key doesn't match (was regenerated)
                console.error('[KeyCache] This usually means the encryption key was regenerated and cannot decrypt old keys');
                throw decryptError;
            }

            const keypairData = JSON.parse(new TextDecoder().decode(decryptedData));
            
            return {
                public_key: keypairData.public_key,
                private_key: keypairData.private_key
            };
        } catch (error) {
            console.error('[KeyCache] ✗ Failed to decrypt cached keypair:', error);
            console.error('[KeyCache] Error details:', {
                name: error.name,
                message: error.message || '(empty message)',
                stack: error.stack,
                account: account ? `${account.slice(0, 6)}...${account.slice(-4)}` : null,
                chainId,
                verifyingContract
            });
            // Remove corrupted cache entry
            await this.removeKeypair(account, chainId, verifyingContract);
            return null;
        }
    }

    /**
     * Remove a cached keypair
     */
    async removeKeypair(account, chainId, verifyingContract) {
        try {
            const cacheKey = `${CACHE_PREFIX}${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            keyStorage().removeItem(cacheKey);
            
            return true;
        } catch (error) {
            console.error('Failed to remove keypair:', error);
            return false;
        }
    }

    /**
     * Clear all cached keypairs (but preserve connection history)
     */
    async clearAll() {
        try {
            // Clear all keys with our prefix, but preserve connection markers
            const keysToRemove = [];
            const storage = keyStorage();
            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (key && key.startsWith(CACHE_PREFIX)) {
                    // Don't remove connection markers - they track first-time vs subsequent connections
                    if (!key.includes('connected_')) {
                        keysToRemove.push(key);
                    }
                }
            }
            
            keysToRemove.forEach(key => storage.removeItem(key));
            
            // Also remove the encryption key
            keyStorage().removeItem(CACHE_KEY_STORAGE_KEY);
            
            return true;
        } catch (error) {
            console.error('Failed to clear cache:', error);
            return false;
        }
    }

    /**
     * Clear all cached keypairs AND connection history (full reset)
     */
    async clearAllIncludingHistory() {
        try {
            // Clear all keys with our prefix including connection markers
            const keysToRemove = [];
            const storage = keyStorage();
            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (key && key.startsWith(CACHE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }
            
            keysToRemove.forEach(key => storage.removeItem(key));
            
            // Also remove the encryption key
            keyStorage().removeItem(CACHE_KEY_STORAGE_KEY);

            const historyKeysToRemove = [];
            const history = historyStorage();
            for (let i = 0; i < history.length; i++) {
                const key = history.key(i);
                if (key && key.startsWith(CACHE_PREFIX)) {
                    historyKeysToRemove.push(key);
                }
            }
            historyKeysToRemove.forEach(key => history.removeItem(key));
            
            return true;
        } catch (error) {
            console.error('Failed to clear cache:', error);
            return false;
        }
    }

    /**
     * Get cache statistics
     */
    async getCacheStats() {
        try {
            let totalEntries = 0;
            const storage = keyStorage();
            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (key && key.startsWith(CACHE_PREFIX)) {
                    totalEntries++;
                }
            }
            
            return {
                totalEntries,
                storageType: 'sessionStorage',
                prefix: CACHE_PREFIX
            };
        } catch (error) {
            console.error('Failed to get cache stats:', error);
            return null;
        }
    }

    /**
     * Check if a wallet+account combination has been connected before
     * This tracks first-time vs subsequent connections
     * Synchronous check for immediate use
     */
    hasBeenConnectedBefore(account, chainId, verifyingContract) {
        try {
            if (!account || !chainId || !verifyingContract) {
                return false;
            }
            const connectionKey = `${CACHE_PREFIX}connected_${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            return !!historyStorage().getItem(connectionKey);
        } catch (error) {
            console.error('Failed to check connection history:', error);
            return false;
        }
    }

    /**
     * Check if keys are cached (synchronous check of sessionStorage)
     * This is a quick check to see if keys exist without async operations
     */
    hasCachedKeysSync(account, chainId, verifyingContract) {
        try {
            if (!account || !chainId || !verifyingContract) {
                return false;
            }
            const cacheKey = `${CACHE_PREFIX}${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            return !!keyStorage().getItem(cacheKey);
        } catch (error) {
            console.error('Failed to check cached keys:', error);
            return false;
        }
    }

    /**
     * Mark a wallet+account combination as having been connected
     * This persists across sessions to track first-time vs subsequent connections
     */
    markAsConnected(account, chainId, verifyingContract) {
        try {
            if (!account || !chainId || !verifyingContract) {
                return false;
            }
            const connectionKey = `${CACHE_PREFIX}connected_${account.toLowerCase()}-${chainId}-${verifyingContract.toLowerCase()}`;
            historyStorage().setItem(connectionKey, JSON.stringify({ timestamp: Date.now() }));
            return true;
        } catch (error) {
            console.error('Failed to mark connection:', error);
            return false;
        }
    }
}

// Export singleton instance
export const secureKeyCache = new SecureKeyCache();
